import { useState } from 'react'
import { Search } from 'lucide-react'
import { Link, useLocation, useSearchParams } from 'wouter'
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  EmptyState,
  SearchInput,
  SegmentedControl,
  SegmentedControlItem,
  SELECT_ALL_VALUE,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  TablePagination,
  TableSkeleton,
  StateCount,
  fromSelectValue,
  humanize,
  FilterBar,
  PageHeader,
  US_STATE_CODES,
  eyebrowClassName,
} from '@ideon-api/ideon-ui'
import { useAccountSearch, useExportAccounts, type AccountListItem } from '../lib/use-account-search'
import { ConfirmMergeDialog } from './duplicate-accounts/ConfirmMergeDialog'

const PRODUCT_LINES = [
  'medical', 'dental', 'vision', 'life', 'disability', 'accident', 'critical_illness', 'hospital_indemnity',
]

// A hostile URL value falls back to the preset instead of reaching the API.
function intInRange(raw: string | null, min: number, max: number): number | undefined {
  if (raw === null || !/^\d+$/.test(raw)) return undefined
  const n = Number(raw)
  return n >= min && n <= max ? n : undefined
}

function oneOf(raw: string | null, allowed: readonly string[]): string | undefined {
  return raw !== null && allowed.includes(raw) ? raw : undefined
}

const COLUMNS = ['Permission sets', 'Small Group', 'Level-Funded', 'Individual', 'Medicare Advantage', 'Ancillary', 'Other']

// The API cannot tell absent from empty, so a 0 count is a dash.
function Count({ n }: { n?: number }) {
  return n ? <StateCount states={n} /> : <>—</>
}

function LicensingCells({ account }: { account: AccountListItem }) {
  const l = account.licensing ?? {}
  const ancillary = l.ancillary ?? []
  // Product lines and features read as one text list, like the wireframe's Other column.
  const other = [
    l.other ? `Large Group Custom ${l.other > 50 ? 'All states' : `${l.other} ${l.other === 1 ? 'state' : 'states'}`}` : '',
    ancillary.map((a) => humanize(a.productLine ?? '')).join(', '),
    (l.features ?? []).map((f) => humanize(f)).join(' · '),
  ].filter(Boolean).join(' · ')
  return (
    <>
      <TableCell className="text-ideon-gray-70">{account.permissionSetCount ?? '—'}</TableCell>
      <TableCell><Count n={l.smallGroup} /></TableCell>
      <TableCell><Count n={l.levelFunded} /></TableCell>
      <TableCell><Count n={l.individual} /></TableCell>
      <TableCell><Count n={l.medicareAdvantage} /></TableCell>
      <TableCell>
        {ancillary.some((a) => a.stateCount) ? (
          <StateCount
            breakdown={ancillary.map((a) => ({ label: humanize(a.productLine ?? ''), states: a.stateCount ?? 0 }))}
          />
        ) : (
          '—'
        )}
      </TableCell>
      <TableCell className="whitespace-normal">{other || '—'}</TableCell>
    </>
  )
}

// Search/pagination/archived-filter state lives in the URL (not local
// useState) so that navigating to a detail page and back restores the exact
// prior view instead of resetting to the default first page. Updates use
// `replace: true` so typing/paging doesn't spam browser history with an
// entry per keystroke.
export function AccountSearchPage() {
  const [searchParams, setSearchParams] = useSearchParams()
  const [, navigate] = useLocation()

  const query = searchParams.get('q') ?? ''
  const page = Number(searchParams.get('page')) || 1
  const perPage = Number(searchParams.get('per_page')) || 50
  const includeArchived = searchParams.get('include_archived') === 'true'

  const now = new Date()
  const currentYear = now.getFullYear()
  const years = Array.from({ length: 5 }, (_, i) => currentYear - 3 + i)
  const filters = {
    year: intInRange(searchParams.get('year'), currentYear - 3, currentYear + 1) ?? currentYear,
    quarter: intInRange(searchParams.get('quarter'), 1, 4) ?? Math.floor(now.getMonth() / 3) + 1,
    productLine: oneOf(searchParams.get('product_line'), PRODUCT_LINES),
    state: oneOf(searchParams.get('state'), US_STATE_CODES),
    licensedOnly: searchParams.get('licensed_only') === 'true',
  }
  const state = useAccountSearch(query, page, perPage, includeArchived, filters)
  const presetYear = currentYear
  const presetQuarter = Math.floor(now.getMonth() / 3) + 1
  const periodChanged = filters.year !== presetYear || filters.quarter !== presetQuarter
  const narrowed = Boolean(query || filters.productLine || filters.state || filters.licensedOnly || periodChanged)
  const { exportCsv, exporting } = useExportAccounts()

  // Checkbox-driven merge selection (PD-30009) -- mirrors DuplicateAccountsPage's
  // Keep/Archive survivor picker, but as a same-page action bar instead of a
  // drawer, since selection here starts from two arbitrary rows rather than a
  // pre-detected pair. Capped at 2: a merge is always exactly two accounts.
  const [selected, setSelected] = useState<AccountListItem[]>([])
  const [survivorId, setSurvivorId] = useState<string | null>(null)
  const [confirmOpen, setConfirmOpen] = useState(false)

  const updateParams = (next: {
    q?: string
    page?: number
    per_page?: number
    include_archived?: boolean
    year?: number
    quarter?: number
    reset_period?: boolean
    product_line?: string
    state?: string
    licensed_only?: boolean
  }) => {
    // Clears any in-progress merge selection on every query/page/page-size/
    // archived-filter change. Without this, `selected` survives into the new
    // result set and `selected.length >= 2 && !isSelected(account)` (below)
    // disables every checkbox on screen with no visible reason -- the two
    // selected accounts are no longer even visible, so it reads as a broken
    // page rather than "you still have a pending merge" (review finding,
    // PD-30009).
    clearSelection()
    setSearchParams(
      (prev) => {
        const params = new URLSearchParams(prev)
        const merged = { q: query, page, per_page: perPage, include_archived: includeArchived, ...next }
        if (merged.q) params.set('q', merged.q)
        else params.delete('q')
        params.set('page', String(merged.page))
        params.set('per_page', String(merged.per_page))
        if (merged.include_archived) params.set('include_archived', 'true')
        else params.delete('include_archived')
        // Licensing params are written only when changed; the preset period
        // is never copied into the URL by an unrelated change.
        if (next.reset_period) {
          params.delete('year')
          params.delete('quarter')
        }
        if (next.year !== undefined) params.set('year', String(next.year))
        if (next.quarter !== undefined) params.set('quarter', String(next.quarter))
        for (const key of ['product_line', 'state'] as const) {
          if (next[key] === undefined) continue
          if (next[key]) params.set(key, next[key])
          else params.delete(key)
        }
        if (next.licensed_only !== undefined) {
          if (next.licensed_only) params.set('licensed_only', 'true')
          else params.delete('licensed_only')
        }
        return params
      },
      { replace: true }
    )
  }

  const handleQueryChange = (value: string) => {
    updateParams({ q: value, page: 1 })
  }

  const handleIncludeArchivedChange = (checked: boolean) => {
    updateParams({ include_archived: checked, page: 1 })
  }

  const isSelected = (account: AccountListItem) => selected.some((a) => a.id === account.id)

  const handleToggle = (account: AccountListItem, checked: boolean) => {
    if (checked) {
      if (selected.length >= 2) return
      setSelected([...selected, account])
    } else {
      setSelected(selected.filter((a) => a.id !== account.id))
      if (survivorId === account.id) setSurvivorId(null)
    }
  }

  const clearSelection = () => {
    setSelected([])
    setSurvivorId(null)
  }

  // Defaults to the first-selected account until the admin explicitly swaps
  // via the "Keep …" toggle below.
  const survivor = selected.length === 2 ? selected.find((a) => a.id === survivorId) ?? selected[0] : null
  const loser = selected.length === 2 ? selected.find((a) => a.id !== survivor?.id) ?? selected[1] : null

  return (
    <>
      <PageHeader title="Accounts" subtitle="Search accounts and check their licensing." />

      <div className="flex flex-col gap-6">
        <FilterBar label="Account filters" stacked>
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3">
            <div className="w-full sm:w-[360px]">
              <SearchInput
                value={query}
                onChange={handleQueryChange}
                placeholder="Search by name or slug"
              />
            </div>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={includeArchived}
                onCheckedChange={(checked) => handleIncludeArchivedChange(checked === true)}
              />
              <span className="text-sm text-ideon-gray-70">Show archived</span>
            </label>
          </div>

          <div role="group" aria-label="Licensing" className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
            <span className={`${eyebrowClassName} text-ideon-gray-70`}>Licensing</span>
            <Select value={String(filters.year)} onValueChange={(v) => updateParams({ year: Number(v), page: 1 })}>
              <SelectTrigger aria-label="Year" className="w-full sm:w-28">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {years.map((y) => (
                  <SelectItem key={y} value={String(y)}>
                    {y}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <SegmentedControl
              aria-label="Quarter"
              value={String(filters.quarter)}
              onValueChange={(v) => updateParams({ quarter: Number(v), page: 1 })}
            >
              {[1, 2, 3, 4].map((q) => (
                <SegmentedControlItem key={q} value={String(q)}>
                  {`Q${q}`}
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
            <Select
              value={filters.productLine ?? SELECT_ALL_VALUE}
              onValueChange={(v) => updateParams({ product_line: fromSelectValue(v), page: 1 })}
            >
              <SelectTrigger aria-label="Product line" className="w-full sm:w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SELECT_ALL_VALUE}>All product lines</SelectItem>
                {PRODUCT_LINES.map((line) => (
                  <SelectItem key={line} value={line}>
                    {humanize(line)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select
              value={filters.state ?? SELECT_ALL_VALUE}
              onValueChange={(v) => updateParams({ state: fromSelectValue(v), page: 1 })}
            >
              <SelectTrigger aria-label="State" className="w-full sm:w-36">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={SELECT_ALL_VALUE}>All states</SelectItem>
                {US_STATE_CODES.map((code) => (
                  <SelectItem key={code} value={code}>
                    {code}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <label className="flex items-center gap-2">
              <Checkbox
                checked={filters.licensedOnly}
                onCheckedChange={(checked) => updateParams({ licensed_only: checked === true, page: 1 })}
              />
              <span className="text-sm text-ideon-gray-70">Licensed only</span>
            </label>
            <Button
              variant="outline"
              className="sm:ml-auto"
              disabled={exporting}
              onClick={() => exportCsv({ q: query, includeArchived, ...filters })}
            >
              Export CSV
            </Button>
          </div>
        </FilterBar>

        {selected.length === 2 && survivor && loser && (
          <div className="flex flex-wrap items-center gap-3 rounded border border-ideon-gray-30 bg-ideon-gray-10 px-4 py-3 max-sm:flex-col max-sm:items-start">
            <span className="text-sm text-ideon-gray-70">Merge selected accounts, keeping:</span>
            <SegmentedControl
              aria-label="Keep which account"
              value={survivor.id}
              onValueChange={setSurvivorId}
            >
              {selected.map((a) => (
                <SegmentedControlItem key={a.id} value={a.id}>
                  {a.name} ({a.slug})
                </SegmentedControlItem>
              ))}
            </SegmentedControl>
            <div className="flex items-center gap-3 sm:ml-auto">
              <Button size="sm" variant="danger" onClick={() => setConfirmOpen(true)}>
                Merge selected
              </Button>
              <Button size="sm" variant="text" onClick={clearSelection}>
                Cancel
              </Button>
            </div>
          </div>
        )}

        {(state.status === 'loading' || (state.status === 'ready' && state.pending && state.data.length === 0)) && (
          <Table density="compact" variant="bordered" className="bg-white">
            <TableHeader>
              <TableRow className="border-ideon-gray-30">
                <TableHead className="w-10" />
                <TableHead className="min-w-[280px] text-ideon-gray-70">Account</TableHead>
                {COLUMNS.map((label) => (
                  <TableHead key={label} className="text-ideon-gray-70">
                    {label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            {/* Matches perPage exactly -- capping this lower than the real
                page size (as a fixed 5 previously did) makes the skeleton
                shorter than the table that replaces it, so the page visibly
                grows on load instead of the swap being invisible. */}
            <TableSkeleton columns={2 + COLUMNS.length} rows={perPage} />
          </Table>
        )}
        {state.status === 'error' && (
          <Alert variant="error" action={<Button onClick={state.reload}>Retry</Button>}>
            Failed to load: {state.message}
          </Alert>
        )}
        {state.status === 'ready' && !state.pending && state.data.length === 0 && (
          <EmptyState
            className="bg-white"
            icon={<Search className="h-10 w-10" />}
            heading={
              query
                ? `No accounts match "${query}"`
                : filters.productLine || filters.state || filters.licensedOnly || periodChanged
                  ? 'No accounts match these filters'
                  : 'No accounts yet'
            }
            description={narrowed ? 'Try a different name or slug, or clear the filters.' : undefined}
            action={
              narrowed ? (
                <Button
                  variant="text"
                  onClick={() => updateParams({ q: '', product_line: '', state: '', licensed_only: false, reset_period: true, page: 1 })}
                >
                  Clear filters
                </Button>
              ) : undefined
            }
          />
        )}
        {state.status === 'ready' && state.data.length > 0 && (
          <div className={`flex flex-col gap-4${state.pending ? ' opacity-60' : ''}`}>
            <Table density="compact" variant="bordered" className="bg-white">
              <TableHeader>
                <TableRow className="border-ideon-gray-30">
                  <TableHead className="w-10" />
                  <TableHead className="min-w-[280px] text-ideon-gray-70">Account</TableHead>
                  {COLUMNS.map((label) => (
                    <TableHead key={label} className="text-ideon-gray-70">
                      {label}
                    </TableHead>
                  ))}
                </TableRow>
              </TableHeader>
              <TableBody>
                {state.data.map((account) => (
                  <TableRow
                    key={account.id}
                    className={
                      account.archived
                        ? 'border-ideon-gray-30'
                        : 'border-ideon-gray-30 cursor-pointer hover:bg-ideon-gray-10'
                    }
                    // Archived accounts 404 on the detail route, so their rows do not navigate.
                    onClick={account.archived ? undefined : () => navigate(`/accounts/${account.slug}`)}
                  >
                    <TableCell onClick={(event) => event.stopPropagation()}>
                      <Checkbox
                        checked={isSelected(account)}
                        disabled={(selected.length >= 2 && !isSelected(account)) || account.archived || state.pending}
                        onCheckedChange={(checked) => handleToggle(account, checked === true)}
                        aria-label={`Select ${account.name} (${account.slug})`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="font-semibold text-ideon-gray-black">
                        {account.archived ? (
                          account.name
                        ) : (
                          <Link
                            href={`/accounts/${account.slug}`}
                            onClick={(event) => event.stopPropagation()}
                            className="hover:underline"
                          >
                            {account.name}
                          </Link>
                        )}
                        {account.archived && (
                          <Badge variant="neutral" dot={false} uppercase={false} className="ml-2">
                            Archived
                          </Badge>
                        )}
                      </div>
                      <div className="text-xs font-normal text-ideon-gray-70">{account.slug}</div>
                    </TableCell>
                    <LicensingCells account={account} />
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <TablePagination
              currentPage={state.meta.page}
              pageSize={state.meta.perPage}
              totalCount={state.meta.totalCount}
              totalPages={state.meta.totalPages}
              onPageChange={(nextPage) => updateParams({ page: nextPage })}
              onPageSizeChange={(size) => updateParams({ per_page: size, page: 1 })}
            />
          </div>
        )}
      </div>

      {survivor && loser && (
        <ConfirmMergeDialog
          isOpen={confirmOpen}
          onClose={() => setConfirmOpen(false)}
          survivor={survivor}
          loser={loser}
          onMerged={() => {
            setConfirmOpen(false)
            clearSelection()
            state.reload()
          }}
        />
      )}
    </>
  )
}
